import { createAsyncThunk } from "@reduxjs/toolkit";

import { getSearchFacets, searchReviews } from "../../../api/reviews";
import { IReviewsBrandID } from "../../../models/nominals";
import {
    IReview,
    ISearchFacet,
    ISearchReviewArgs,
} from "../../../models/reviews";

/**
 * Search and load reviews with filters.
 *
 * Performs a search for reviews based on the provided query parameters (filters, sorting,
 * pagination) and fetches the available facet options. Runs both API calls in parallel
 * for optimal performance.
 *
 * The query supports:
 * - Pagination: page number and page size
 * - Filtering: product_id, product_variant_id, rating, source_id, etc.
 * - Sorting: most recent, highest rated, most helpful, etc.
 *
 * @param {Object} params - The parameters object
 * @param {IReviewsBrandID} params.brandID - The brand identifier to search within
 * @param {ISearchReviewArgs} params.query - The search query with filters and pagination
 *
 * @returns {Promise} Promise resolving to an object containing:
 *   - reviews: Array of review objects matching the query
 *   - facets: Available facet options (for building filter UI)
 *   - hasMore: Whether more pages of results exist
 *   - page: Current page number
 *   - loadedFacetValues: The query parameters used (for tracking)
 *
 * @example
 * // Load first page of 5-star reviews
 * dispatch(loadReviews({
 *   brandID: 'tempur-pedic',
 *   query: {
 *     page: 1,
 *     rating: '5',
 *     sort: 'most-recent'
 *   }
 * }));
 *
 * @sideEffects
 * - Updates `data.reviews` with fetched reviews (replaces on page 1, appends on page 2+) via extraReducers
 * - Updates `data.facets` with available filter options via extraReducers
 * - Updates `ui.page`, `ui.hasMore`, and `ui.loadedFacetValues` with pagination state via extraReducers
 * - Sets `data.loadedInitialReviews` to true via extraReducers
 */
export const loadReviews = createAsyncThunk(
    "reviews/loadReviews",
    async ({
        brandID,
        query,
    }: {
        brandID: IReviewsBrandID;
        query: ISearchReviewArgs;
    }): Promise<{
        reviews: readonly IReview[];
        facets: readonly ISearchFacet[];
        hasMore: boolean;
        page: number;
        loadedFacetValues: ISearchReviewArgs;
    }> => {
        const frozenQuery = Object.freeze({ ...query });
        const [reviewData, facets] = await Promise.all([
            searchReviews(query),
            getSearchFacets(brandID, query),
        ]);

        if (!reviewData || !facets) {
            throw new Error("Failed to load reviews data");
        }
        return {
            reviews: reviewData.results,
            facets: facets,
            hasMore: reviewData.next !== null,
            page: frozenQuery.page || 1,
            loadedFacetValues: frozenQuery,
        };
    },
);
